import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Minus, Percent, RefreshCw, Landmark, Calculator } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import RefiImpactDialog from "./RefiImpactDialog";

/**
 * Current interest rates — daily-refreshed snapshot, relevant to any CRE
 * investor evaluating refinance / acquisition math.
 *
 * Ordering is intentional: headline benchmark first (10Y Treasury), then
 * SOFR (CRE floating-rate index), then the 30Y mortgage consumer headline,
 * then the 2Y / 5Y / 30Y treasuries for curve context.
 */
const ROWS = [
  { key: "treasury_10y", label: "10-Year Treasury", hint: "CRE valuation benchmark" },
  { key: "sofr", label: "SOFR", hint: "Overnight secured — CRE floating rate index" },
  { key: "mortgage_30y", label: "30-Year Fixed Mortgage", hint: "Freddie Mac PMMS, weekly" },
  { key: "treasury_2y", label: "2-Year Treasury", hint: "Short end of the curve" },
  { key: "treasury_5y", label: "5-Year Treasury", hint: "Mid-curve" },
  { key: "treasury_30y", label: "30-Year Treasury", hint: "Long end of the curve" },
];

function ChangeChip({ bps }) {
  if (bps === null || bps === undefined || Number.isNaN(bps)) {
    return <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 inline-flex items-center gap-0.5"><Minus className="w-3 h-3" />—</span>;
  }
  const v = Number(bps);
  if (Math.abs(v) < 0.5) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] font-mono uppercase tracking-widest text-slate-500">
        <Minus className="w-3 h-3" /> unch
      </span>
    );
  }
  const up = v > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-mono uppercase tracking-widest ${up ? "text-red-600" : "text-emerald-600"}`}>
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {up ? "+" : ""}{v.toFixed(0)} bps
    </span>
  );
}

function formatAgo(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diff / 3600000);
  if (h < 1) return "Just now";
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function InterestRatesWidget() {
  const { user } = useAuth();
  const isInvestor = user?.role === "landlord";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refiOpen, setRefiOpen] = useState(false);
  const [refiTarget, setRefiTarget] = useState(null); // { rate, label }

  const load = () => {
    setLoading(true);
    api.get("/rates/current")
      .then(({ data }) => setData(data))
      .catch(() => setData({ available: false }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openRefi = (rateRow, label) => {
    if (!isInvestor) return; // only investors have loans to reprice
    setRefiTarget({ rate: rateRow.rate, label });
    setRefiOpen(true);
  };

  const visible = data?.available
    ? ROWS.filter((r) => data[r.key]?.rate !== undefined && data[r.key]?.rate !== null)
    : [];

  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid="interest-rates-widget">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Landmark className="w-4 h-4 text-slate-500" />
          <div>
            <div className="overline text-slate-500">Markets</div>
            <h3 className="text-sm font-bold text-slate-900">Interest rates today</h3>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">
            {data?.fetched_at ? `Updated ${formatAgo(data.fetched_at)}` : "—"}
          </span>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="text-slate-500 hover:text-slate-900 p-1 rounded-sm"
            title="Refresh"
            data-testid="interest-rates-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="px-5 py-10 text-center text-sm text-slate-400" data-testid="interest-rates-loading">Loading rates…</div>
      ) : !data?.available || visible.length === 0 ? (
        <div className="px-5 py-10 text-center" data-testid="interest-rates-empty">
          <Percent className="w-5 h-5 text-slate-300 mx-auto mb-2" />
          <div className="text-sm text-slate-600">Market data is briefly unavailable.</div>
          <div className="text-xs text-slate-400 mt-1">Try again in a few minutes — we'll have a fresh snapshot by 6am UTC tomorrow.</div>
        </div>
      ) : (
        <ul className="divide-y divide-slate-100" data-testid="interest-rates-list">
          {visible.map((r) => {
            const row = data[r.key];
            const clickable = isInvestor;
            const content = (
              <>
                <div className="min-w-0 text-left">
                  <div className="text-sm font-medium text-slate-900 flex items-center gap-1">
                    {r.label}
                    {clickable && <Calculator className="w-3 h-3 text-slate-300 group-hover:text-slate-900 transition" />}
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{r.hint}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xl font-bold text-slate-900 tabular-nums font-mono">
                    {row.rate.toFixed(2)}<span className="text-xs text-slate-500 ml-0.5">%</span>
                  </div>
                  <ChangeChip bps={row.change_bps} />
                </div>
              </>
            );
            return (
              <li key={r.key} data-testid={`interest-rate-row-${r.key}`}>
                {clickable ? (
                  <button
                    type="button"
                    onClick={() => openRefi(row, r.label)}
                    className="group w-full px-5 py-3 flex items-center justify-between gap-3 hover:bg-slate-50 transition cursor-pointer"
                    title="Click to see refinance impact across your loans"
                    data-testid={`interest-rate-refi-${r.key}`}
                  >
                    {content}
                  </button>
                ) : (
                  <div className="px-5 py-3 flex items-center justify-between gap-3">
                    {content}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <RefiImpactDialog
        open={refiOpen}
        rate={refiTarget?.rate}
        rateLabel={refiTarget?.label}
        onClose={() => setRefiOpen(false)}
      />
    </div>
  );
}
